import React, { useState, useEffect } from "react";
import { getAuth, updateProfile } from "firebase/auth";
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  runTransaction,
} from "firebase/firestore";
import { db } from "./firebase-config";
import dayjs from "dayjs";
import {
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import { AddCircleOutlineRounded, Edit, Save } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "./App.css";

function Profile({ parentId }) {
  const [kids, setKids] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    schoolGrade: "",
    class: "",
    birthDate: dayjs(),
    baptismName: "",
    baptismDate: dayjs(),
    foodAllergies: "",
    hasAllergicReactionAsthmaAnaphylaxis: false,
    allergyNotes: "",
    mediaConsent: true,
    fatherName: "",
    fatherHouseName: "",
    fatherParishIndia: "",
    fatherDioceseIndia: "",
    fatherEmail: "",
    fatherPhoneNo: "",
    motherName: "",
    motherHouseName: "",
    motherEmail: "",
    motherPhoneNo: "",
    siblingsDetails: "",
    homeAddress: "",
    homeSuburb: "",
    homePostcode: "",
    emergencyContactName: "",
    emergencyContactPhoneNo: "",
    parishFamilyRegNo: "",
  });
  const [editId, setEditId] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [officialFullName, setOfficialFullName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [loadingParent, setLoadingParent] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [blueCardNo, setBlueCardNo] = useState("");
  const [blueCardExpiry, setBlueCardExpiry] = useState(dayjs());
  const [originalBlueCardExpiry, setOriginalBlueCardExpiry] = useState("");
  const [gender, setGender] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(dayjs());
  const [profession, setProfession] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [catechismExperienceInAustralia, setCatechismExperienceInAustralia] =
    useState("");
  const [safeguardingTrainingCompleted, setSafeguardingTrainingCompleted] =
    useState("");
  const [dateOfSafeguardingTraining, setDateOfSafeguardingTraining] =
    useState(dayjs());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isBirthDateValid, setIsBirthDateValid] = useState(true);
  const [isBaptismDateValid, setIsBaptismDateValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(true);
  const schoolGrades = [
    "Prep",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
    "Pass Out",
  ];
  const currentYear = dayjs().year().toString();

  useEffect(() => {
    if (!parentId) return;

    const fetchUserData = async () => {
      try {
        // Fetch the displayName from the users collection
        const userDocRef = doc(db, "users", parentId);
        const userDoc = await getDoc(userDocRef);
        let userEmail = "";
        if (userDoc.exists()) {
          userEmail = userDoc.data().email;
          setParentEmail(userEmail);
          setDisplayName(userDoc.data().displayName);
          setOfficialFullName(
            userDoc.data().officialFullName
              ? userDoc.data().officialFullName
              : "",
          );
          setBlueCardNo(
            userDoc.data().blueCardNo ? userDoc.data().blueCardNo : "",
          );
          setBlueCardExpiry(
            userDoc.data().blueCardExpiry
              ? dayjs(userDoc.data().blueCardExpiry)
              : dayjs(),
          );
          setOriginalBlueCardExpiry(userDoc.data().blueCardExpiry || "");
          setGender(userDoc.data().gender ? userDoc.data().gender : "");
          setDateOfBirth(
            userDoc.data().dateOfBirth
              ? dayjs(userDoc.data().dateOfBirth)
              : dayjs(),
          );
          setProfession(
            userDoc.data().profession ? userDoc.data().profession : "",
          );
          setMobileNo(userDoc.data().mobileNo ? userDoc.data().mobileNo : "");
          setCatechismExperienceInAustralia(
            userDoc.data().catechismExperienceInAustralia
              ? userDoc.data().catechismExperienceInAustralia
              : "",
          );
          setSafeguardingTrainingCompleted(
            userDoc.data().safeguardingTrainingCompleted
              ? userDoc.data().safeguardingTrainingCompleted
              : "",
          );
          setDateOfSafeguardingTraining(
            userDoc.data().dateOfSafeguardingTraining
              ? dayjs(userDoc.data().dateOfSafeguardingTraining)
              : dayjs(),
          );
          setUserRoles(userDoc.data().roles);

          // Fetch the list of students with the userEmail in fatherEmail, motherEmail or guardianEmails
          const fatherEmailQuery = query(
            collection(db, "students"),
            where("fatherEmail", "==", userEmail),
          );
          const motherEmailQuery = query(
            collection(db, "students"),
            where("motherEmail", "==", userEmail),
          );
          const guardianEmailQuery = query(
            collection(db, "students"),
            where("guardianEmail", "==", userEmail),
          );

          const [
            fatherEmailSnapshot,
            motherEmailSnapshot,
            guardianEmailSnapshot,
          ] = await Promise.all([
            getDocs(fatherEmailQuery),
            getDocs(motherEmailQuery),
            getDocs(guardianEmailQuery),
          ]);

          const studentSet = new Set();
          [
            ...fatherEmailSnapshot.docs,
            ...motherEmailSnapshot.docs,
            ...guardianEmailSnapshot.docs,
          ].map((doc) => {
            const student = {
              id: doc.id,
              ...doc.data(),
            };
            studentSet.add(JSON.stringify(student));
            return student;
          });

          const uniqueKidsData = Array.from(studentSet).map((student) =>
            JSON.parse(student),
          );

          // Fetch all attendance records for the current year
          const attendanceQuery = query(
            collection(db, "attendance"),
            where("__name__", ">=", `${currentYear}-01-01`),
            where("__name__", "<=", `${currentYear}-12-31`),
          );
          const attendanceSnapshot = await getDocs(attendanceQuery);
          const attendanceRecords = attendanceSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Process attendance data to calculate absences for each student
          const kidsWithAbsences = uniqueKidsData.map((student) => {
            const absences = attendanceRecords.filter(
              (record) => record.records[student.regNo] === false,
            );
            const nrAbsences = absences.length;
            return { ...student, nrAbsences };
          });

          setKids(kidsWithAbsences);
        }
      } catch (error) {
        console.error("Error fetching user data: ", error);
      } finally {
        setLoadingParent(false);
      }
    };

    fetchUserData();
  }, [parentId, currentYear]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "homePostcode") {
      // Allow empty or partial numeric input while typing; formatting happens on blur.
      if (value === "") {
        setFormData((prevData) => {
          const newData = { ...prevData, [name]: "" };
          setIsFormValid(validate(newData));
          return newData;
        });
      } else if (/^\d*$/.test(value)) {
        setFormData((prevData) => {
          const newData = { ...prevData, [name]: value };
          setIsFormValid(validate(newData));
          return newData;
        });
      }
    } else {
      setFormData((prevData) => {
        const newValue = type === "checkbox" ? checked : value;
        const newData = { ...prevData, [name]: newValue };

        // If user just indicated allergic reaction but allergyNotes empty, show snackbar
        if (
          name === "hasAllergicReactionAsthmaAnaphylaxis" &&
          (newValue === true || newValue === "true") &&
          (!newData.allergyNotes || String(newData.allergyNotes).trim() === "")
        ) {
          setSnackbarMessage("Enter details in Allergy Notes");
          setSnackbarOpen(true);
        }

        setIsFormValid(validate(newData));
        return newData;
      });
    }
  };

  const handleDateChange = (name, value) => {
    const today = dayjs();
    if (name === "birthDate" && value.isAfter(today)) {
      setSnackbarMessage("Birth date cannot be in the future.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsBirthDateValid(false);
      setIsFormValid(false);
      return;
    }
    if (
      name === "baptismDate" &&
      (value.isBefore(formData.birthDate) || value.isAfter(today))
    ) {
      setSnackbarMessage("Baptism date must be between birth date and today.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsBaptismDateValid(false);
      setIsFormValid(false);
      return;
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setIsBirthDateValid(true);
    setIsBaptismDateValid(true);
    setIsFormValid(true);
  };

  const handleProfileSave = async () => {
    if (!displayName) {
      setSnackbarMessage("Please fill in Name.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (
      userRoles.includes("teacher") ||
      userRoles.includes("principal") ||
      userRoles.includes("director") ||
      userRoles.includes("volunteer") ||
      userRoles.includes("first-aid")
    ) {
      if (!blueCardNo) {
        setSnackbarMessage("Please fill in Blue Card No.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      if (!blueCardExpiry.isAfter(dayjs(), "day")) {
        setSnackbarMessage("Blue Card Expiry Date should be in future.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    }
    try {
      const userRef = doc(db, "users", parentId);

      // Update the displayName in Firebase Authentication
      const auth = getAuth();
      const user = auth.currentUser;
      await updateProfile(user, {
        displayName: displayName,
      });

      if (
        userRoles.includes("teacher") ||
        userRoles.includes("principal") ||
        userRoles.includes("director") ||
        userRoles.includes("volunteer") ||
        userRoles.includes("first-aid")
      ) {
        await updateDoc(userRef, {
          displayName: displayName,
          officialFullName: officialFullName,
          blueCardNo: blueCardNo,
          blueCardExpiry: blueCardExpiry.format("YYYY-MM-DD"),
          ...(blueCardExpiry.format("YYYY-MM-DD") !== originalBlueCardExpiry && {
            blueCardReminderSent: false,
            blueCardSecondReminderSent: false,
          }),
          gender: gender,
          dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
          profession: profession,
          mobileNo: mobileNo,
          // cast to number before storing
          catechismExperienceInAustralia:
            Number(catechismExperienceInAustralia) || 0,
          safeguardingTrainingCompleted: safeguardingTrainingCompleted,
          dateOfSafeguardingTraining:
            safeguardingTrainingCompleted === "Yes"
              ? dateOfSafeguardingTraining.format("YYYY-MM-DD")
              : "",
        });
      } else {
        await updateDoc(userRef, {
          displayName: displayName,
          officialFullName: officialFullName,
        });
      }

      setSnackbarMessage("User details saved successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving user data:", error);
      setSnackbarMessage("Failed to save user details. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSave = async () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.gender ||
      !formData.schoolGrade ||
      !formData.fatherName ||
      !formData.fatherEmail ||
      !formData.fatherPhoneNo ||
      !formData.motherName ||
      !formData.motherEmail ||
      !formData.motherPhoneNo ||
      !formData.emergencyContactName ||
      !formData.emergencyContactPhoneNo ||
      !formData.parishFamilyRegNo
    ) {
      setSnackbarMessage("Please fill in all required fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      let studentData = {
        ...formData,
        schoolGradeYear: dayjs().year(),
        guardianEmail: parentEmail,
        status: formData.schoolGrade === "Pass Out" ? "Pass Out" : "Active",
      };
      // Format the birthDate and baptismDate to YYYY-MM-DD string
      studentData.birthDate =
        !studentData.birthDate || studentData.birthDate.isSame(dayjs(), "day")
          ? ""
          : studentData.birthDate.format("YYYY-MM-DD");

      studentData.baptismDate =
        !studentData.baptismDate ||
          studentData.baptismDate.isSame(dayjs(), "day")
          ? ""
          : studentData.baptismDate.format("YYYY-MM-DD");

      if (editId) {
        // Update existing student
        await updateDoc(doc(db, "students", editId), studentData);
        setSnackbarMessage("Kid's details updated successfully!");
        setKids((prevStudents) =>
          prevStudents.map((student) =>
            student.id === editId ? { ...student, ...formData } : student,
          ),
        );
      } else {
        // Generate regNo for new student using transaction for atomicity
        const currentYear = dayjs().year();
        const counterDocRef = doc(db, "counters", `regNo-${currentYear}`);

        const newRegNo = await runTransaction(db, async (transaction) => {
          const counterDoc = await transaction.get(counterDocRef);
          let nextNumber = 1;

          if (counterDoc.exists()) {
            nextNumber = counterDoc.data().count + 1;
          }

          transaction.set(
            counterDocRef,
            { count: nextNumber },
            { merge: true },
          );

          return `SMSC-${currentYear}-${String(nextNumber).padStart(3, "0")}`;
        });

        studentData.regNo = newRegNo;

        // Add new student
        const newDocRef = await addDoc(collection(db, "students"), studentData);
        setSnackbarMessage("Kid added successfully!");

        // Add the new student to the state
        setKids((prevKids) => [
          ...prevKids,
          { id: newDocRef.id, ...studentData },
        ]);
      }
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpen(false);
      setFormData({
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "",
        schoolGrade: "",
        class: "",
        foodAllergies: "",
        hasAllergicReactionAsthmaAnaphylaxis: false,
        allergyNotes: "",
        mediaConsent: true,
        fatherName: "",
        fatherHouseName: "",
        fatherParishIndia: "",
        fatherDioceseIndia: "",
        fatherEmail: "",
        fatherPhoneNo: "",
        motherName: "",
        motherHouseName: "",
        motherEmail: "",
        motherPhoneNo: "",
        siblingsDetails: "",
        homeAddress: "",
        homeSuburb: "",
        homePostcode: "",
        emergencyContactName: "",
        emergencyContactPhoneNo: "",
        birthDate: dayjs(),
        baptismName: "",
        baptismDate: dayjs(),
        parishFamilyRegNo: "",
      });
      setEditId(null);
      setIsFormValid(true);
    } catch (error) {
      console.error("Error saving kid's details: ", error);
      setSnackbarMessage("Failed to save details.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEdit = (kid) => {
    setFormData({
      firstName: kid.firstName,
      middleName: kid.middleName || "",
      lastName: kid.lastName,
      gender: kid.gender || "",
      schoolGrade: kid.schoolGrade || "",
      class: kid.class || "",
      foodAllergies: kid.foodAllergies || "",
      hasAllergicReactionAsthmaAnaphylaxis:
        kid.hasAllergicReactionAsthmaAnaphylaxis,
      allergyNotes: kid.allergyNotes || "",
      mediaConsent: kid.mediaConsent,
      fatherName: kid.fatherName || "",
      fatherHouseName: kid.fatherHouseName || "",
      fatherParishIndia: kid.fatherParishIndia || "",
      fatherDioceseIndia: kid.fatherDioceseIndia || "",
      fatherEmail: kid.fatherEmail || "",
      fatherPhoneNo: kid.fatherPhoneNo || "",
      motherName: kid.motherName || "",
      motherHouseName: kid.motherHouseName || "",
      motherEmail: kid.motherEmail || "",
      motherPhoneNo: kid.motherPhoneNo || "",
      siblingsDetails: kid.siblingsDetails || "",
      homeAddress: kid.homeAddress || "",
      homeSuburb: kid.homeSuburb || "",
      homePostcode: kid.homePostcode || "",
      emergencyContactName: kid.emergencyContactName || "",
      emergencyContactPhoneNo: kid.emergencyContactPhoneNo || "",
      birthDate: kid.birthDate ? dayjs(kid.birthDate) : dayjs(),
      baptismName: kid.baptismName || "",
      baptismDate: kid.baptismDate ? dayjs(kid.baptismDate) : dayjs(),
      parishFamilyRegNo: kid.parishFamilyRegNo || "",
    });
    setEditId(kid.id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      gender: "",
      schoolGrade: "",
      class: "",
      foodAllergies: "",
      hasAllergicReactionAsthmaAnaphylaxis: false,
      allergyNotes: "",
      mediaConsent: true,
      fatherName: "",
      fatherHouseName: "",
      fatherParishIndia: "",
      fatherDioceseIndia: "",
      fatherEmail: "",
      fatherPhoneNo: "",
      motherName: "",
      motherHouseName: "",
      motherEmail: "",
      motherPhoneNo: "",
      siblingsDetails: "",
      homeAddress: "",
      homeSuburb: "",
      homePostcode: "",
      emergencyContactName: "",
      emergencyContactPhoneNo: "",
      birthDate: dayjs(),
      baptismName: "",
      baptismDate: dayjs(),
    });
    setEditId(null);
    setIsFormValid(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const validate = (newData) => {
    const requiredFields = [
      "firstName",
      "lastName",
      "gender",
      "schoolGrade",
      "fatherName",
      "fatherEmail",
      "fatherPhoneNo",
      "motherName",
      "motherEmail",
      "motherPhoneNo",
      "emergencyContactName",
      "emergencyContactPhoneNo",
      "parishFamilyRegNo",
    ];

    for (const field of requiredFields) {
      const val = newData[field];
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
    }

    // If allergic reaction is true, allergyNotes is required
    if (
      newData.hasAllergicReactionAsthmaAnaphylaxis &&
      (!newData.allergyNotes || String(newData.allergyNotes).trim() === "")
    ) {
      return false;
    }

    // parishFamilyRegNo must be "NA" or match format SMSC followed by 3 digits 001-999
    if (
      newData.parishFamilyRegNo &&
      newData.parishFamilyRegNo.trim() !== "NA" &&
      !/^SMSC(?!000)\d{3}$/.test(newData.parishFamilyRegNo)
    ) {
      return false;
    }

    return true;
  };

  const handlePostcodeBlur = () => {
    const val = formData.homePostcode;
    if (!val) return;
    if (/^\d+$/.test(val)) {
      const intValue = parseInt(val, 10);
      if (intValue >= 100 && intValue <= 9999) {
        const formattedValue = String(intValue).padStart(4, "0");
        setFormData((prevData) => {
          const newData = { ...prevData, homePostcode: formattedValue };
          setIsFormValid(validate(newData));
          return newData;
        });
      }
    }
  };

  const isFieldError = (field) => {
    const val = formData[field];
    if (field === "allergyNotes") {
      return (
        formData.hasAllergicReactionAsthmaAnaphylaxis &&
        (!val || String(val).trim() === "")
      );
    }
    if (field === "parishFamilyRegNo") {
      if (!val || typeof val !== "string" || val.trim() === "") return true;
      const trimmedVal = val.trim();
      return trimmedVal !== "NA" && !/^SMSC(?!000)\d{3}$/.test(trimmedVal);
    }
    if (typeof val === "string") return val.trim() === "";
    return val === undefined || val === null;
  };

  return (
    <Container maxWidth="lg" className="app-wrapper">
      {/* Personal Information Section */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Personal Information</Typography>
          <Button
            id="save-profile-button"
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={handleProfileSave}
            disabled={!parentId}
          >
            Save
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          {/* Name Field */}
          <TextField
            id="display-name-textfield"
            margin="dense"
            label="Name"
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
          />

          {/* Volunteer/Teacher/Principal/Director/First-aid Fields */}
          {(userRoles.includes("volunteer") ||
            userRoles.includes("teacher") ||
            userRoles.includes("principal") ||
            userRoles.includes("director") ||
            userRoles.includes("first-aid")) && (
              <>
                <TextField
                  id="official-full-name-textfield"
                  margin="dense"
                  label="Official Full Name"
                  name="officialFullName"
                  value={officialFullName}
                  onChange={(e) => setOfficialFullName(e.target.value)}
                  fullWidth
                />
                {/* Blue Card Fields */}
                <TextField
                  id="blue-card-no-textfield"
                  margin="dense"
                  label="Blue Card No:"
                  name="blueCardNo"
                  value={blueCardNo}
                  onChange={(e) => setBlueCardNo(e.target.value)}
                  fullWidth
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    id="blue-card-expiry-datepicker"
                    label="Blue Card Expiry"
                    value={blueCardExpiry}
                    name="blueCardExpiry"
                    onChange={(newValue) => setBlueCardExpiry(newValue)}
                    format="DD-MM-YYYY"
                    inputFormat="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: "dense",
                      },
                    }}
                  />
                </LocalizationProvider>

                {/* Personal Fields */}
                <FormControl fullWidth margin="dense">
                  <InputLabel id="volunteer-gender-label">Gender</InputLabel>
                  <Select
                    id="volunteer-gender-select"
                    labelId="volunteer-gender-label"
                    label="Gender"
                    name="gender"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <MenuItem
                      id="volunteer-gender-blank"
                      key=""
                      value=""
                    ></MenuItem>
                    <MenuItem id="volunteer-gender-male" key="Male" value="Male">
                      Male
                    </MenuItem>
                    <MenuItem
                      id="volunteer-gender-female"
                      key="Female"
                      value="Female"
                    >
                      Female
                    </MenuItem>
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    id="volunteer-dob-datepicker"
                    label="Date of Birth"
                    value={dateOfBirth}
                    name="dateOfBirth"
                    onChange={(newValue) => setDateOfBirth(newValue)}
                    format="DD-MM-YYYY"
                    inputFormat="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: "dense",
                      },
                    }}
                  />
                </LocalizationProvider>
                <TextField
                  id="volunteer-profession-textfield"
                  margin="dense"
                  label="Profession"
                  name="profession"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  fullWidth
                />
                <TextField
                  id="volunteer-mobile-no-textfield"
                  margin="dense"
                  label="Mobile No"
                  name="mobileNo"
                  value={mobileNo}
                  onChange={(e) => setMobileNo(e.target.value)}
                  fullWidth
                />

                {/* Experience Fields */}
                <TextField
                  id="volunteer-catechism-experience-textfield"
                  margin="dense"
                  label="Catechism Experience In Australia (Years)"
                  name="catechismExperienceInAustralia"
                  type="number"
                  value={catechismExperienceInAustralia}
                  onChange={(e) =>
                    setCatechismExperienceInAustralia(e.target.value)
                  }
                  fullWidth
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                />
                <FormControl fullWidth margin="dense">
                  <InputLabel id="volunteer-safeguarding-label">
                    Safeguarding Training Completed
                  </InputLabel>
                  <Select
                    id="volunteer-safeguarding-select"
                    labelId="volunteer-safeguarding-label"
                    label="Safeguarding Training Completed"
                    name="safeguardingTrainingCompleted"
                    value={safeguardingTrainingCompleted}
                    onChange={(e) =>
                      setSafeguardingTrainingCompleted(e.target.value)
                    }
                  >
                    <MenuItem
                      id="volunteer-safeguarding-blank"
                      key=""
                      value=""
                    ></MenuItem>
                    <MenuItem
                      id="volunteer-safeguarding-yes"
                      key="Yes"
                      value="Yes"
                    >
                      Yes
                    </MenuItem>
                    <MenuItem id="volunteer-safeguarding-no" key="No" value="No">
                      No
                    </MenuItem>
                  </Select>
                </FormControl>
                {safeguardingTrainingCompleted === "Yes" && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      id="volunteer-safeguarding-date-datepicker"
                      label="Date of Safeguarding Training"
                      value={dateOfSafeguardingTraining}
                      name="dateOfSafeguardingTraining"
                      onChange={(newValue) =>
                        setDateOfSafeguardingTraining(newValue)
                      }
                      format="DD-MM-YYYY"
                      inputFormat="YYYY-MM-DD"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: "dense",
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </>
            )}
        </Box>
      </Box>
      {kids.length > 0 && (
        <>
          <Typography align="center" gutterBottom>
            Your Kids
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  Name
                </TableCell>
                <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  Absences
                </TableCell>
                <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kids.map((kid) => (
                <TableRow id={`kid-${kid.id}`} key={kid.id}>
                  <TableCell>
                    <Typography>{`${kid.firstName} ${kid.middleName || ""} ${kid.lastName
                      }`}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {kid.regNo ? kid.regNo : ""} (
                      {kid.class ? kid.class : kid.schoolGrade})
                    </Typography>
                  </TableCell>
                  <TableCell>{kid.nrAbsences}</TableCell>
                  <TableCell>
                    <IconButton
                      id={`edit-kid-${kid.id}`}
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEdit(kid)}
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      <Box
        sx={{
          mt: 3,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Button
          id="add-kid-button"
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineRounded />}
          onClick={() => setOpen(true)}
          disabled={loadingParent || !parentEmail}
        >
          Add Kid
        </Button>
        {!loadingParent && !parentEmail && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Please refresh page if 'Add Kid' is disabled.
          </Typography>
        )}
      </Box>
      {kids.some((kid) => !kid.feesPaid) &&
        kids.filter((kid) => kid.status === "Active").length > 0 && (
          <Typography
            variant="body1"
            color="error"
            style={{ marginTop: "1rem" }}
          >
            Please transfer{" "}
            <b>
              $
              {(() => {
                const activeKidsCount = kids.filter(
                  (kid) => kid.status === "Active",
                ).length;
                if (activeKidsCount === 1) {
                  return 150;
                }
                if (activeKidsCount === 2) return 200;
                return 250;
              })()}
            </b>{" "}
            to the following bank account:
            <br />
            Account Name: St Marys Catechism
            <br />
            BSB: 064-786
            <br />
            Account Number: 100028218
          </Typography>
        )}
      <Typography variant="body1" color="primary" style={{ marginTop: "1rem" }}>
        By providing details of your kid(s); you are agreeing to the following:
        <ul>
          <li>
            I hereby give my son/daughter the permission to attend catechism
            classes organised by St. Mary's Syro Malabar Mission, Sunshine
            Coast, at its Sunday School.
          </li>
          <li>
            I understand that I shall make necessary transport arrangements to
            and from the Sunday School venue on time. Sunday School is conducted
            along with the Holy Qurbana.
          </li>
          <li>
            I am aware of the content of study and nature of the classes and
            examinations/assignments being organised and have no objection for
            my child to undertake such study.
          </li>
          <li>
            I agree for my child to attend special events/picnics/charity
            activities organised by the Sunday School.
          </li>
          <li>
            As a parent or legal guardian, I acknowledge that the primary
            responsibility for the safeguarding of my child(ren) rests with me.
            I understand and agree that Catechism teachers and staff are
            responsible for the safety and supervision of children strictly
            during the scheduled Catechism class hours only.
          </li>
          <li>
            I agree for my child to be photographed for Sunday School or Parish
            Bulletins/Magazines or official website of the parish [Unless you
            selected 'No' for Media Consent for the child].
          </li>
          <li>
            I agree to follow the Safeguarding policies and Protocols of the
            Parish I agree for my child to be prepared for the Holy Sacraments.
          </li>
          <li>
            I certify that the information provided is true and that no
            information relating to the child has been omitted.
          </li>
          <li>
            As the parent or legal guardian of the child, I have read this
            agreement and fully understand the contents herein.
          </li>
        </ul>
      </Typography>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editId ? "Edit Kid" : "Add Kid"}</DialogTitle>
        <DialogContent>
          <FormControl
            id="first-name-form-control"
            fullWidth
            margin="dense"
            error={isFieldError("firstName")}
          >
            <TextField
              id="first-name-textfield"
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={isFieldError("firstName")}
              required
            />
          </FormControl>
          <TextField
            id="middle-name-textfield"
            margin="dense"
            label="Middle Name"
            name="middleName"
            fullWidth
            value={formData.middleName}
            onChange={handleChange}
          />
          <FormControl
            id="last-name-form-control"
            fullWidth
            margin="dense"
            error={isFieldError("lastName")}
          >
            <TextField
              id="last-name-textfield"
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={isFieldError("lastName")}
              required
            />
          </FormControl>
          <FormControl id="gender-form-control" fullWidth margin="dense" error={!formData.gender}>
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              id="gender-select"
              labelId="gender-label"
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <MenuItem id="gender-blank" key="" value=""></MenuItem>
              <MenuItem id="gender-male" key="Male" value="Male">
                Male
              </MenuItem>
              <MenuItem id="gender-female" key="Female" value="Female">
                Female
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl
            id="school-grade-form-control"
            fullWidth
            margin="normal"
            error={isFieldError("schoolGrade")}
          >
            <InputLabel id="school-grade-label">School Grade</InputLabel>
            <Select
              id="school-grade-select"
              labelId="school-grade-label"
              label="School Grade"
              name="schoolGrade"
              value={formData.schoolGrade}
              onChange={handleChange}
              required
            >
              {schoolGrades.map((schoolGrade) => (
                <MenuItem
                  id={`school-grade-menu-item-${schoolGrade.replace(
                    /\s+/g,
                    "-",
                  )}`}
                  key={schoolGrade}
                  value={schoolGrade}
                >
                  {schoolGrade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              id="birth-date-datepicker"
              label="Date of Birth"
              value={formData.birthDate}
              name="birthDate"
              onChange={(newValue) => handleDateChange("birthDate", newValue)}
              format="DD-MM-YYYY"
              inputFormat="YYYY-MM-DD"
              fullWidth
              error={!isBirthDateValid}
              helperText={
                !isBirthDateValid && "Please enter valid Date of Birth."
              }
            />
            <TextField
              id="baptism-name-textfield"
              margin="dense"
              label="Baptism Name"
              name="baptismName"
              fullWidth
              value={formData.baptismName}
              onChange={handleChange}
            />
            <DatePicker
              id="baptism-date-datepicker"
              label="Date of Baptism"
              value={formData.baptismDate}
              name="baptismDate"
              onChange={(newValue) => handleDateChange("baptismDate", newValue)}
              format="DD-MM-YYYY"
              inputFormat="YYYY-MM-DD"
              fullWidth
              error={!isBaptismDateValid}
              helperText={
                !isBaptismDateValid && "Please enter valid Date of Baptism."
              }
            />
            <TextField
              id="food-allergies-textfield"
              margin="dense"
              label="Food Allergies"
              name="foodAllergies"
              fullWidth
              value={formData.foodAllergies}
              onChange={handleChange}
            />
            <FormControl
              id="has-allergic-reaction-form-control"
              fullWidth
              margin="normal"
            >
              <InputLabel id="has-allergic-reaction-label">
                Has Allergic reaction, Asthma, or Anaphylaxis
              </InputLabel>
              <Select
                id="has-allergic-reaction-select"
                labelId="has-allergic-reaction-label"
                value={formData.hasAllergicReactionAsthmaAnaphylaxis}
                onChange={handleChange}
                name="hasAllergicReactionAsthmaAnaphylaxis"
              >
                <MenuItem
                  id="has-allergic-reaction-true"
                  key={true}
                  value={true}
                >
                  Yes
                </MenuItem>
                <MenuItem
                  id="has-allergic-reaction-false"
                  key={false}
                  value={false}
                >
                  No
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              id="allergy-notes-textfield"
              margin="dense"
              label="Allergy Notes"
              name="allergyNotes"
              fullWidth
              value={formData.allergyNotes}
              onChange={handleChange}
              required={formData.hasAllergicReactionAsthmaAnaphylaxis}
              error={isFieldError("allergyNotes")}
            />
            <FormControl
              id="media-consent-form-control"
              fullWidth
              margin="normal"
            >
              <InputLabel id="dialog-media-consent-label">
                Media consent given for photos/videos
              </InputLabel>
              <Select
                id="dialog-media-consent-select"
                labelId="dialog-media-consent-label"
                value={formData.mediaConsent}
                onChange={handleChange}
                name="mediaConsent"
              >
                <MenuItem
                  id="dialog-media-consent-true"
                  key={true}
                  value={true}
                >
                  Yes
                </MenuItem>
                <MenuItem
                  id="dialog-media-consent-false"
                  key={false}
                  value={false}
                >
                  No
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              id="parish-family-regno-textfield"
              margin="dense"
              label="Parish Family Reg No."
              name="parishFamilyRegNo"
              fullWidth
              value={formData.parishFamilyRegNo}
              onChange={handleChange}
              helperText="Ask your family unit co-ordinator if you don't know."
              error={isFieldError("parishFamilyRegNo")}
              required
            />
          </LocalizationProvider>
          <TextField
            id="father-name-textfield"
            margin="dense"
            label="Father's Name"
            name="fatherName"
            fullWidth
            value={formData.fatherName}
            onChange={handleChange}
            required
            error={isFieldError("fatherName")}
          />
          <TextField
            id="father-house-name-textfield"
            margin="dense"
            label="Father's House Name"
            name="fatherHouseName"
            fullWidth
            value={formData.fatherHouseName}
            onChange={handleChange}
          />
          <TextField
            id="father-email-textfield"
            margin="dense"
            label="Father's Email"
            name="fatherEmail"
            fullWidth
            value={formData.fatherEmail}
            onChange={handleChange}
            required
            error={isFieldError("fatherEmail")}
          />
          <TextField
            id="father-phone-no-textfield"
            margin="dense"
            label="Father's Phone Number"
            name="fatherPhoneNo"
            fullWidth
            value={formData.fatherPhoneNo}
            onChange={handleChange}
            required
            error={isFieldError("fatherPhoneNo")}
          />
          <TextField
            id="mother-name-textfield"
            margin="dense"
            label="Mother's Name"
            name="motherName"
            fullWidth
            value={formData.motherName}
            onChange={handleChange}
            required
            error={isFieldError("motherName")}
          />
          <TextField
            id="mother-email-textfield"
            margin="dense"
            label="Mother's Email"
            name="motherEmail"
            fullWidth
            value={formData.motherEmail}
            onChange={handleChange}
            required
            error={isFieldError("motherEmail")}
          />
          <TextField
            id="mother-phone-no-textfield"
            margin="dense"
            label="Mother's Phone Number"
            name="motherPhoneNo"
            fullWidth
            value={formData.motherPhoneNo}
            onChange={handleChange}
            required
            error={isFieldError("motherPhoneNo")}
          />
          <TextField
            id="home-address-textfield"
            margin="dense"
            label="Home Address"
            name="homeAddress"
            fullWidth
            value={formData.homeAddress}
            onChange={handleChange}
          />
          <TextField
            id="home-suburb-textfield"
            margin="dense"
            label="Home Suburb"
            name="homeSuburb"
            fullWidth
            value={formData.homeSuburb}
            onChange={handleChange}
          />
          <TextField
            id="home-postcode-textfield"
            margin="dense"
            label="Home Postcode"
            name="homePostcode"
            fullWidth
            value={formData.homePostcode}
            onChange={handleChange}
            onBlur={handlePostcodeBlur}
          />
          <TextField
            id="siblings-details-textfield"
            margin="dense"
            label="Siblings Details"
            name="siblingsDetails"
            fullWidth
            value={formData.siblingsDetails}
            onChange={handleChange}
          />
          <TextField
            id="emergency-contact-name-textfield"
            margin="dense"
            label="Emergency Contact Name"
            name="emergencyContactName"
            fullWidth
            value={formData.emergencyContactName}
            onChange={handleChange}
            required
            error={isFieldError("emergencyContactName")}
          />
          <TextField
            id="emergency-contact-phone-no-textfield"
            margin="dense"
            label="Emergency Contact Phone Number"
            name="emergencyContactPhoneNo"
            fullWidth
            value={formData.emergencyContactPhoneNo}
            onChange={handleChange}
            required
            error={isFieldError("emergencyContactPhoneNo")}
          />
          <TextField
            id="father-parish-india-textfield"
            margin="dense"
            label="Father's Parish (India)"
            name="fatherParishIndia"
            fullWidth
            value={formData.fatherParishIndia}
            onChange={handleChange}
          />
          <TextField
            id="father-diocese-india-textfield"
            margin="dense"
            label="Father's Diocese (India)"
            name="fatherDioceseIndia"
            fullWidth
            value={formData.fatherDioceseIndia}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            id="dialog-button-close"
            onClick={handleClose}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            id="dialog-button-save"
            onClick={handleSave}
            color="primary"
            disabled={!isFormValid}
          >
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Profile;
